/**
 * Lazy loader for speech.js (browser speech-to-text behind the composer
 * mic). Same shape as actions-loader: the main bundle never imports the
 * recorder; the chunk is fetched on the first mic tap and cached.
 */
import type { createVoiceRecorder } from './voice-recorder';

export type SpeechModule = { createVoiceRecorder: typeof createVoiceRecorder };

let chunkBase: string | null = null;
let cached: Promise<SpeechModule> | null = null;

export function setSpeechChunkBase(base: string): void {
  chunkBase = base.replace(/\/$/, '');
}

export function loadSpeech(): Promise<SpeechModule> {
  if (!cached) {
    if (!chunkBase) {
      return Promise.reject(new Error('[showpilot] speech chunk base not configured'));
    }
    const url = `${chunkBase}/speech.js`;
    cached = import(/* @vite-ignore */ url)
      .then((m: Partial<SpeechModule>) => {
        if (typeof m.createVoiceRecorder !== 'function') {
          throw new Error('[showpilot] speech chunk loaded but module shape is wrong');
        }
        return m as SpeechModule;
      })
      .catch((err: unknown) => {
        cached = null;
        throw err;
      });
  }
  return cached;
}

/** True when this browser has the Web Speech API (a property read, no chunk). */
export function speechSupported(): boolean {
  const w = window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown };
  return Boolean(w.SpeechRecognition ?? w.webkitSpeechRecognition);
}

export function resetSpeechLoaderForTests(): void {
  chunkBase = null;
  cached = null;
}
